/**
 * force-variant-selection.js
 *
 * Obriga o cliente a selecionar as opcoes de variante (principalmente o TAMANHO)
 * antes de adicionar ao carrinho. Progressive enhancement em JS puro, sem
 * dependencia do bundle Vite nem do Alpine.
 *
 * Cobre: PDP principal, botao "comprar" sticky, quick-view e formularios
 * injetados dinamicamente. Sobrevive ao swap de section que acontece na troca
 * de variante (o tema re-renderiza o product-info via fetch).
 *
 * Toda opcao com MAIS DE UM valor e considerada obrigatoria. No load tratamos
 * cada grupo obrigatorio como NAO escolhido pelo usuario, mesmo que o Shopify
 * tenha pre-marcado um radio (selected_or_first_available_variant). Somente
 * apos o usuario interagir (change/click real) o grupo e liberado.
 */
(function () {
  'use strict';

  var ORIGINAL_TEXT_ATTR = 'data-fvs-original-text';
  var ORIGINAL_LABEL_ATTR = 'data-fvs-original-aria';
  var BOUND_FORM_ATTR = 'data-fvs-bound';
  var ERROR_CLASS = 'fvs-error';
  var ALERT_CLASS = 'fvs-alert';

  /**
   * Store de "grupos tocados", persistido em memoria (nao no DOM). E essencial
   * porque o JS do tema REPLACES os blocos [data-product-block] (variant-picker
   * e product-form) a cada troca de variante — qualquer estado guardado no DOM
   * (data-attribute ou listener) e perdido no swap. A chave e
   * `${productId}::${optionName}`. Como a primeira interacao do usuario e o que
   * dispara o swap, gravamos o "tocado" ANTES do swap; no re-init lemos daqui e
   * mantemos o grupo liberado.
   */
  var touchedStore = Object.create(null);

  function touchKey(productId, optionName) {
    return String(productId) + '::' + String(optionName);
  }

  /** Strings i18n com fallback pt-BR. */
  function strings() {
    var loc =
      (window.auroraThemeLocales && window.auroraThemeLocales.variantStrings) || {};
    return {
      selectSize: loc.selectSize || 'Selecione o tamanho',
      selectOption: loc.selectOption || 'Selecione {option}',
      selectOptions: loc.selectOptions || 'Selecione as opcoes',
      selectSizeError:
        loc.selectSizeError || 'Selecione o tamanho antes de adicionar ao carrinho.',
      selectOptionError:
        loc.selectOptionError ||
        'Selecione {option} antes de adicionar ao carrinho.',
      selectOptionsError:
        loc.selectOptionsError ||
        'Selecione as opcoes antes de adicionar ao carrinho.'
    };
  }

  /** Nomes de opcao considerados "tamanho" (para o rotulo especifico). */
  function isSizeOption(name) {
    if (!name) return false;
    var n = name.toLowerCase().trim();
    return (
      n === 'tamanho' ||
      n === 'size' ||
      n === 'talla' ||
      n === 'taille' ||
      n === 'größe' ||
      n === 'grosse' ||
      n === 'numeração' ||
      n === 'numeracao'
    );
  }

  function fillTemplate(tpl, value) {
    return String(tpl).replace('{option}', value);
  }

  /* ----------------------------------------------------------------------- *
   * Descoberta de elementos
   * ----------------------------------------------------------------------- */

  /** Encontra o <form> de compra dentro de um <product-form>. */
  function getForm(productForm) {
    return productForm.querySelector('form[data-type="add-to-cart-form"]') ||
      productForm.querySelector('form');
  }

  /**
   * Localiza o <variant-picker> associado a um form.
   * Estrategia:
   *  1. Pelos radios cujo atributo form aponta para o id do form (mais seguro).
   *  2. Fallback: o <variant-picker> na mesma section (mesmo container product-info
   *     / quick-view), casando data-product-id.
   */
  function getVariantPicker(productForm, form) {
    var formId = form && form.id;
    if (formId) {
      var radio = document.querySelector(
        'input[type="radio"][form="' + cssEscape(formId) + '"]'
      );
      if (radio) {
        var vp = radio.closest('variant-picker');
        if (vp) return vp;
      }
    }
    // Fallback pela section/produto.
    var productId = productForm.getAttribute('data-product-id');
    var scope = productForm.closest('product-info') ||
      productForm.closest('.quick-view') ||
      document;
    if (productId) {
      var pickers = scope.querySelectorAll(
        'variant-picker[data-product-id="' + cssEscape(productId) + '"]'
      );
      if (pickers.length) return pickers[0];
    }
    return scope.querySelector ? scope.querySelector('variant-picker') : null;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    // Fallback simples para os ids do tema (sem caracteres exoticos alem de ---).
    return String(value).replace(/["\\]/g, '\\$&');
  }

  /**
   * Retorna os grupos de opcao obrigatorios. Cada grupo e uma lista de radios
   * que compartilham o mesmo name. Consideramos obrigatorio o grupo com mais de
   * um radio (mais de um valor).
   */
  function getRequiredGroups(picker) {
    var groups = [];
    if (!picker) return groups;
    var containers = picker.querySelectorAll(
      '[data-variant-picker-option-inputs]:not([data-fvs-optional])'
    );
    for (var i = 0; i < containers.length; i++) {
      var radios = containers[i].querySelectorAll('input[type="radio"]');
      if (radios.length > 1) {
        var name =
          (radios[0] && radios[0].getAttribute('name')) || '';
        // Nome da opcao = name sem o sufixo "-{position}".
        var optionName = name.replace(/-\d+$/, '');
        groups.push({
          container: containers[i],
          radios: radios,
          name: name,
          optionName: optionName
        });
      }
    }
    return groups;
  }

  function getButtons(productForm) {
    // Todos os submit de add-to-cart (principal + sticky). Buscamos tambem no
    // <product-form-button> que fica dentro do product-form.
    return productForm.querySelectorAll('button[type="submit"][name="add"]');
  }

  function getDynamicCheckout(productForm) {
    return productForm.querySelector('.dynamic-checkout-buttons');
  }

  /* ----------------------------------------------------------------------- *
   * Estado dos grupos
   * ----------------------------------------------------------------------- */

  function isGroupTouched(ctx, group) {
    return touchedStore[touchKey(ctx.productId, group.optionName)] === true;
  }

  function markGroupTouched(ctx, group) {
    touchedStore[touchKey(ctx.productId, group.optionName)] = true;
  }

  function pendingGroups(ctx) {
    var pending = [];
    for (var i = 0; i < ctx.groups.length; i++) {
      if (!isGroupTouched(ctx, ctx.groups[i])) pending.push(ctx.groups[i]);
    }
    return pending;
  }

  /* ----------------------------------------------------------------------- *
   * Rotulos dos botoes
   * ----------------------------------------------------------------------- */

  function pendingButtonLabel(pending, s) {
    if (pending.length === 1) {
      var g = pending[0];
      if (isSizeOption(g.optionName)) return s.selectSize;
      return fillTemplate(s.selectOption, g.optionName);
    }
    return s.selectOptions;
  }

  function pendingErrorMessage(pending, s) {
    if (pending.length === 1) {
      var g = pending[0];
      if (isSizeOption(g.optionName)) return s.selectSizeError;
      return fillTemplate(s.selectOptionError, g.optionName);
    }
    return s.selectOptionsError;
  }

  /** Guarda e substitui o texto do botao pelo rotulo dinamico de "pendente". */
  function applyPendingButtonText(button, label) {
    var textEl = button.querySelector('[data-button-text]') || button;

    // Guarda o HTML original apenas uma vez, para poder restaurar depois.
    if (!button.hasAttribute(ORIGINAL_TEXT_ATTR)) {
      button.setAttribute(ORIGINAL_TEXT_ATTR, textEl.innerHTML);
    }
    if (!button.hasAttribute(ORIGINAL_LABEL_ATTR)) {
      button.setAttribute(
        ORIGINAL_LABEL_ATTR,
        button.getAttribute('aria-label') || ''
      );
    }

    // Se houver spans de rotulo curto/longo (botao sticky), atualiza ambos.
    var longEl = textEl.querySelector('.product-buy-btn__text-label');
    var shortEl = textEl.querySelector('.product-buy-btn__text-label-short');
    if (longEl || shortEl) {
      if (longEl) longEl.textContent = ' ' + label;
      if (shortEl) shortEl.textContent = ' ' + label;
    } else {
      textEl.textContent = label;
    }
    button.setAttribute('aria-label', label);
  }

  /** Restaura o texto original do botao (salvo antes de trocar). */
  function restoreButtonText(button) {
    var textEl = button.querySelector('[data-button-text]') || button;
    if (button.hasAttribute(ORIGINAL_TEXT_ATTR)) {
      textEl.innerHTML = button.getAttribute(ORIGINAL_TEXT_ATTR);
    }
    if (button.hasAttribute(ORIGINAL_LABEL_ATTR)) {
      var original = button.getAttribute(ORIGINAL_LABEL_ATTR);
      if (original) {
        button.setAttribute('aria-label', original);
      } else {
        button.removeAttribute('aria-label');
      }
    }
  }

  /* ----------------------------------------------------------------------- *
   * Mensagens de erro / destaque
   * ----------------------------------------------------------------------- */

  function showGroupError(group, message) {
    group.container.classList.add(ERROR_CLASS);
    group.container.setAttribute('aria-invalid', 'true');

    var alert = group.container.querySelector('.' + ALERT_CLASS);
    if (!alert) {
      alert = document.createElement('p');
      alert.className = ALERT_CLASS;
      alert.setAttribute('role', 'alert');
      alert.setAttribute('aria-live', 'assertive');
      group.container.appendChild(alert);
    }
    alert.textContent = message;
    alert.hidden = false;
  }

  function clearGroupError(group) {
    group.container.classList.remove(ERROR_CLASS);
    group.container.removeAttribute('aria-invalid');
    var alert = group.container.querySelector('.' + ALERT_CLASS);
    if (alert) alert.hidden = true;
  }

  /* ----------------------------------------------------------------------- *
   * Visual das opcoes (nao deixar tamanho pre-marcado)
   * ----------------------------------------------------------------------- */

  function checkedRadio(group) {
    for (var i = 0; i < group.radios.length; i++) {
      if (group.radios[i].checked) return group.radios[i];
    }
    return null;
  }

  /**
   * Shopify pre-marca o radio da primeira variante disponivel. Mantemos o
   * radio (o VariantPicker do tema precisa dele para montar a URL), mas
   * removemos a classe `selected` ate o usuario escolher de fato.
   */
  function syncVisualSelection(ctx) {
    var s = strings();
    for (var i = 0; i < ctx.groups.length; i++) {
      var group = ctx.groups[i];
      var pending = !isGroupTouched(ctx, group);
      var labelsContainer = findLabelsContainer(ctx, group);
      var checked = checkedRadio(group);
      var labels;
      var j;
      var el;
      var dropdownLabel;

      if (labelsContainer) {
        labels = labelsContainer.querySelectorAll('label');
        for (j = 0; j < labels.length; j++) {
          el = labels[j];
          if (pending) {
            el.classList.remove('selected');
          } else if (checked) {
            el.classList.toggle('selected', el.getAttribute('for') === checked.id);
          }
        }

        dropdownLabel = labelsContainer.querySelector(
          '[data-dropdown-variant-picker-label] span'
        );
        if (dropdownLabel) {
          if (pending) {
            dropdownLabel.textContent = isSizeOption(group.optionName)
              ? s.selectSize
              : fillTemplate(s.selectOption, group.optionName);
          } else if (checked) {
            dropdownLabel.textContent = checked.value;
          }
        }
      }
    }
  }

  /* ----------------------------------------------------------------------- *
   * Aplicacao do estado (trava / libera)
   * ----------------------------------------------------------------------- */

  function refreshState(ctx) {
    syncVisualSelection(ctx);

    var pending = pendingGroups(ctx);
    var s = strings();
    var buttons = getButtons(ctx.productForm);
    var i;

    if (pending.length > 0) {
      var label = pendingButtonLabel(pending, s);
      for (i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        // Nao mexemos em botoes ja indisponiveis por esgotamento (sold out):
        // se o Shopify desabilitou por indisponibilidade, marcamos como nosso
        // tambem para reter o estado, mas so trocamos texto se ainda nao
        // estava travado por sold out. Aqui sempre reforcamos a trava por
        // opcao pendente.
        btn.disabled = true;
        btn.setAttribute('data-fvs-locked', 'true');
        applyPendingButtonText(btn, label);
      }
      // Bloqueia o dynamic checkout (best-effort).
      var dc = getDynamicCheckout(ctx.productForm);
      if (dc) {
        dc.setAttribute('disabled', '');
        dc.classList.add('fvs-dc-locked');
        dc.setAttribute('aria-disabled', 'true');
      }
    } else {
      for (i = 0; i < buttons.length; i++) {
        var b = buttons[i];
        if (b.getAttribute('data-fvs-locked') === 'true') {
          b.removeAttribute('data-fvs-locked');
          restoreButtonText(b);
          // So reabilita se nao houver outra razao (o proprio texto original
          // ja reflete sold-out/available; se estava sold out o texto original
          // era "Esgotado" e mantemos desabilitado).
          if (!isVariantUnavailable(ctx, b)) {
            b.disabled = false;
          } else {
            b.disabled = true;
          }
        }
      }
      var dc2 = getDynamicCheckout(ctx.productForm);
      if (dc2) {
        dc2.removeAttribute('disabled');
        dc2.classList.remove('fvs-dc-locked');
        dc2.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Detecta se a variante atualmente selecionada esta indisponivel, para nao
   * reabilitar um botao que o tema deixou desabilitado por sold-out. Usamos o
   * JSON de variante selecionada renderizado pelo variant-picker.
   */
  function isVariantUnavailable(ctx, button) {
    if (!ctx.picker) return false;
    var json = ctx.picker.querySelector('[data-selected-variant-json]');
    if (!json) return false;
    try {
      var data = JSON.parse(json.textContent);
      if (data && data.available === false) return true;
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  /* ----------------------------------------------------------------------- *
   * Listeners
   * ----------------------------------------------------------------------- */

  function onGroupInteraction(ctx, group) {
    if (isGroupTouched(ctx, group)) return;
    markGroupTouched(ctx, group);
    clearGroupError(group);
    refreshState(ctx);
  }

  function bindGroup(ctx, group) {
    var handler = function () {
      onGroupInteraction(ctx, group);
    };
    // change nos radios cobre teclado + programatico (o click numa pill/swatch
    // dispara change no radio associado via atributo "for").
    for (var i = 0; i < group.radios.length; i++) {
      group.radios[i].addEventListener('change', handler);
    }
    // Click de seguranca no container de inputs (caso haja label direto ali).
    group.container.addEventListener('click', handler);

    // Tambem bindamos o container de LABELS correspondente (pills, swatches,
    // dropdown). Ele vive em [data-variant-picker-option-labels] com o atributo
    // option-picker-name = nome da opcao. Cobre swatches que fazem selecao via
    // JS sem necessariamente disparar change no radio.
    var labels = findLabelsContainer(ctx, group);
    if (labels) {
      labels.addEventListener('click', function (e) {
        if (
          e.target &&
          e.target.closest(
            'label, .pill, shape-swatch, [data-color-swatches-picker-label], ' +
              '.color-swatch-select-parent, .image-swatches__item, ' +
              '.variant-picker-dropdown__item'
          )
        ) {
          onGroupInteraction(ctx, group);
        }
      });
      labels.addEventListener('keydown', function (e) {
        // Enter/Espaco em label focavel = selecao.
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          if (e.target && e.target.closest('label, [tabindex]')) {
            onGroupInteraction(ctx, group);
          }
        }
      });
    }
  }

  /** Encontra o container de labels (pills/swatches) para uma opcao. */
  function findLabelsContainer(ctx, group) {
    if (!ctx.picker) return null;
    var all = ctx.picker.querySelectorAll('[data-variant-picker-option-labels]');
    // Match por option-picker-name (usado nos pills/dropdown padrao).
    for (var i = 0; i < all.length; i++) {
      var name = all[i].getAttribute('option-picker-name');
      if (name && name.toLowerCase().trim() === group.optionName.toLowerCase().trim()) {
        return all[i];
      }
    }
    // Fallback: pela ordem (mesmo indice do grupo).
    var idx = ctx.groups.indexOf(group);
    if (idx > -1 && all[idx]) return all[idx];
    return null;
  }

  /**
   * Interceptador de submit em fase de CAPTURA. Trava a prova de teclado:
   * se houver grupo pendente, cancela o submit antes de qualquer outro handler
   * (inclusive o ProductForm do bundle) e destaca os grupos.
   */
  function onSubmitCapture(ctx, e) {
    var pending = pendingGroups(ctx);
    if (pending.length === 0) return; // tudo escolhido -> deixa passar.

    e.preventDefault();
    e.stopImmediatePropagation();

    var s = strings();
    var message = pendingErrorMessage(pending, s);
    for (var i = 0; i < pending.length; i++) {
      showGroupError(pending[i], message);
    }

    // Reforca o estado dos botoes/rotulos.
    refreshState(ctx);

    // Foco + scroll no primeiro pendente.
    var first = pending[0];
    var focusTarget =
      first.container.querySelector('input[type="radio"], [tabindex], label') ||
      first.container;
    try {
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    } catch (err) {
      /* ignore */
    }
    if (first.container.scrollIntoView) {
      first.container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* ----------------------------------------------------------------------- *
   * Inicializacao por product-form
   * ----------------------------------------------------------------------- */

  function initProductForm(productForm) {
    if (productForm && productForm.closest('[data-product-card-size-picker]')) {
      return;
    }

    if (!productForm || productForm.getAttribute(BOUND_FORM_ATTR) === 'true') {
      // Ja vinculado: apenas re-sincroniza o estado (util apos swap parcial).
      if (productForm && productForm.__fvsCtx) {
        refreshState(productForm.__fvsCtx);
      }
      return;
    }

    var form = getForm(productForm);
    if (!form) return;

    var picker = getVariantPicker(productForm, form);
    var groups = getRequiredGroups(picker);

    // Produto de variante unica (has_only_default_variant) ou sem grupos com
    // mais de um valor: nada a travar.
    if (!groups.length) {
      productForm.setAttribute(BOUND_FORM_ATTR, 'true');
      return;
    }

    var productId =
      productForm.getAttribute('data-product-id') ||
      (picker && picker.getAttribute('data-product-id')) ||
      'default';

    var ctx = {
      productForm: productForm,
      form: form,
      picker: picker,
      groups: groups,
      productId: productId
    };
    productForm.__fvsCtx = ctx;

    for (var i = 0; i < groups.length; i++) {
      bindGroup(ctx, groups[i]);
    }

    // Trava em fase de captura no proprio form.
    form.addEventListener(
      'submit',
      function (e) {
        onSubmitCapture(ctx, e);
      },
      true
    );

    productForm.setAttribute(BOUND_FORM_ATTR, 'true');

    // Estado inicial travado.
    refreshState(ctx);

    // Observa o swap de section: quando o product-info re-renderiza, o
    // product-form antigo e removido e um novo entra; o observer global cuida
    // de reinit. Aqui observamos tambem o proprio product-form para reaplicar
    // o rotulo caso o tema reescreva o texto do botao na troca de variante.
    observeButtonText(ctx);
  }

  /**
   * Apos o swap de variante, o JS do tema pode reescrever o texto do botao
   * (ex.: "Adicionar ao carrinho" / "Esgotado"). Reaplicamos nosso rotulo se
   * ainda houver opcao pendente.
   */
  function observeButtonText(ctx) {
    var buttons = getButtons(ctx.productForm);
    if (!buttons.length) return;

    var mo = new MutationObserver(function () {
      if (ctx.__fvsReapplying) return;
      var pending = pendingGroups(ctx);
      if (pending.length === 0) return;
      ctx.__fvsReapplying = true;
      // Se o tema restaurou o texto/removeu o disabled, reforca nossa trava.
      refreshState(ctx);
      // libera flag no proximo tick para nao entrar em loop com o proprio set.
      window.setTimeout(function () {
        ctx.__fvsReapplying = false;
      }, 0);
    });

    for (var i = 0; i < buttons.length; i++) {
      mo.observe(buttons[i], {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-label']
      });
    }
    ctx.__fvsButtonObserver = mo;
  }

  /* ----------------------------------------------------------------------- *
   * Varredura + observers globais
   * ----------------------------------------------------------------------- */

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var forms = scope.querySelectorAll('product-form');
    for (var i = 0; i < forms.length; i++) {
      initProductForm(forms[i]);
    }
    // Se o proprio root e um product-form (node injetado direto).
    if (root && root.matches && root.matches('product-form')) {
      initProductForm(root);
    }
  }

  /**
   * Observer global: cobre quick-view injetado, section-swap na troca de
   * variante (product-info re-renderizado) e qualquer product-form adicionado
   * depois.
   */
  function startGlobalObserver() {
    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('product-form')) {
            initProductForm(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    scan(document);
    startGlobalObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
